import React, { useCallback } from 'react';

import ExerciseExecuteCode from '../../containers/ExerciseExecuteCode';
import { canSubmitExercise, isExamExercise } from '../../helpers/examUtilities';

import useCodeEditorShortcuts from './useCodeEditorShortcuts';

type Props = {
  active?: boolean;
  canExplainSolution: boolean;
  canRun: boolean;
  canSubmit: boolean;
  ckey: string;
  code?: string;
  currentLineValue?: string;
  disableExecution: boolean;
  focus: (...args: any[]) => any;
  hideOpenAIBranding: boolean;
  id: number;
  isMarkdownExercise?: boolean;
  isSolution?: boolean;
  language: string;
  markdown?: {
    activeTitle?: string;
  };
  nbAttempts?: number;
  onExplainSolution: (...args: any[]) => any;
  onRunCode: (...args: any[]) => any;
  onSubmitCode: (...args: any[]) => any;
  onSubmitShortcut: (...args: any[]) => any;
  preExerciseCode?: string;
  resetCode?: string;
  sct?: string;
  selectedCode: string;
  setCurrentCode: (...args: any[]) => any;
  solution?: string;
  type?: string;
  updateAttempts: (...args: any[]) => any;
  updateConsoleCode: (...args: any[]) => any;
};

const CodeEditorControlButtons: React.FC<Props> = ({
  active,
  canExplainSolution,
  canRun,
  canSubmit,
  ckey,
  code,
  currentLineValue,
  disableExecution,
  focus,
  hideOpenAIBranding,
  id,
  isMarkdownExercise,
  isSolution,
  language,
  markdown,
  nbAttempts,
  onExplainSolution,
  onRunCode,
  onSubmitCode,
  onSubmitShortcut,
  preExerciseCode,
  resetCode,
  sct,
  selectedCode,
  setCurrentCode,
  solution,
  type,
  updateAttempts,
  updateConsoleCode,
}) => {
  const isExampleExerciseType = isExamExercise(type);

  const onReset = useCallback(() => {
    setCurrentCode(resetCode);
  }, [setCurrentCode, resetCode]);

  const submitCode = useCallback(
    ({ codeToExecute, command, runOnly }) => {
      const format =
        isMarkdownExercise && markdown && markdown.activeTitle
          ? markdown.activeTitle.split(' ').pop()
          : undefined;

      onSubmitCode({
        language,
        code: codeToExecute,
        solution,
        sct,
        type,
        echo: true,
        command,
        pre_exercise_code: preExerciseCode,
        id,
        format,
        runOnly,
        isSolution,
      });
    },
    [
      isMarkdownExercise,
      onSubmitCode,
      language,
      solution,
      sct,
      type,
      preExerciseCode,
      id,
      isSolution,
      markdown,
    ],
  );

  const onRun = useCallback(
    ({ didUseKeyboardShortcut } = {}) => {
      const hasUserSelectedCode =
        selectedCode != null && selectedCode.length > 0;

      onRunCode({
        shouldMoveCursorToNextLine:
          didUseKeyboardShortcut && !hasUserSelectedCode,
      });

      const getCodeToExecute = () => {
        if (hasUserSelectedCode) {
          return selectedCode;
        }
        if (didUseKeyboardShortcut) {
          return currentLineValue;
        }
        return code;
      };

      const codeToExecute = getCodeToExecute();

      submitCode({
        command: language === 'r' ? 'run' : 'console',
        codeToExecute,
        runOnly: true,
      });
      if (language === 'python') {
        updateConsoleCode({
          output: { type: 'code', payload: codeToExecute },
        });
      }
    },
    [
      onRunCode,
      submitCode,
      language,
      code,
      updateConsoleCode,
      selectedCode,
      currentLineValue,
    ],
  );

  const onSubmit = useCallback(() => {
    if (isExampleExerciseType) {
      if (!canSubmitExercise(nbAttempts)) {
        return;
      }
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      updateAttempts(nbAttempts + 1);
    }
    submitCode({
      command: 'submit',
      codeToExecute: code,
    });
  }, [code, isExampleExerciseType, nbAttempts, submitCode, updateAttempts]);

  const onExplainSolutionCb = useCallback(() => {
    if (language === 'sql') {
      onExplainSolution('consoleSqlTabs', 'code explanation');
    } else {
      onExplainSolution('consoleTabs', 'Code Explanation');
    }
  }, [language, onExplainSolution]);

  useCodeEditorShortcuts({
    canSubmit,
    canRun,
    language,
    onSubmitShortcut,
    active,
    onRun,
    onSubmit,
    focus,
    disableExecution,
  });

  return (
    <ExerciseExecuteCode
      id={id}
      key={`submit_button_${id}`}
      ckey={ckey}
      canExplainSolution={canExplainSolution}
      canRun={canRun}
      canSubmit={canSubmit}
      onExplainSolution={onExplainSolutionCb}
      onReset={onReset}
      onRun={onRun}
      onSubmit={onSubmit}
      nbAttempts={isExampleExerciseType ? nbAttempts : undefined}
      isMarkdownExercise={isMarkdownExercise}
      isSolution={isSolution}
      disableSubmit={isExampleExerciseType && !canSubmitExercise(nbAttempts)}
      disableExecution={disableExecution}
      hideOpenAIBranding={hideOpenAIBranding}
    />
  );
};

export default CodeEditorControlButtons;
